import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { AppLayout } from '../layouts/AppLayout';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { Progress } from '../components/ui/progress';
import { Skeleton } from '../components/ui/skeleton';
import { Textarea } from '../components/ui/textarea';
import {
  Brain, FileText, Clock, Shield, AlertTriangle, CheckCircle,
  Loader2, Sparkles, Target, Scale, History, TrendingUp,
  Download, Copy, RefreshCw, ChevronRight, ArrowLeft
} from 'lucide-react';
import { toast } from 'sonner';

const AI_FUNCTIONS = [
  { id: 'classify', name: 'Classificador', icon: Brain, description: 'Classificação automática de atos' },
  { id: 'deadlines', name: 'Extrator de Prazos', icon: Clock, description: 'Identificação de prazos' },
  { id: 'plan', name: 'Planejador', icon: Target, description: 'Geração de plano de ação' },
  { id: 'evidence', name: 'Validador de Evidências', icon: CheckCircle, description: 'Análise de evidências' },
  { id: 'draft', name: 'Minuta Técnica', icon: FileText, description: 'Resposta técnica formal' },
  { id: 'summary', name: 'Resumo Executivo', icon: TrendingUp, description: 'Para Governador e Casa Civil' },
  { id: 'risk', name: 'Previsão de Risco', icon: AlertTriangle, description: 'Análise de riscos' },
  { id: 'precedents', name: 'Memória Institucional', icon: History, description: 'Busca de precedentes' },
];

export const AIEnginePage = () => {
  const { actId } = useParams();
  const navigate = useNavigate();
  const { api } = useAuth();
  
  const [act, setAct] = useState(null);
  const [loading, setLoading] = useState(true);
  const [activeFunction, setActiveFunction] = useState('classify');
  const [processing, setProcessing] = useState(false);
  const [results, setResults] = useState({});

  useEffect(() => {
    const fetchAct = async () => {
      if (!actId) {
        setLoading(false);
        return;
      }
      try {
        const response = await api.get(`/acts/${actId}`);
        setAct(response.data);
      } catch (error) {
        console.error('Error fetching act:', error);
        toast.error('Erro ao carregar ato');
      } finally {
        setLoading(false);
      }
    };
    fetchAct();
  }, [actId, api]);

  const runAIFunction = async (functionId) => {
    if (!actId) {
      toast.error('Selecione um ato primeiro');
      return;
    }

    setProcessing(true);
    setActiveFunction(functionId);

    try {
      let endpoint = '';
      switch (functionId) {
        case 'classify':
          endpoint = `/ai/engine/classify?description=${encodeURIComponent(act?.description || '')}`;
          break;
        case 'deadlines':
          const deadlineRes = await api.post('/ai/engine/extract-deadlines', null, {
            params: { text: act?.description || '' }
          });
          setResults(prev => ({ ...prev, deadlines: deadlineRes.data }));
          toast.success('Prazos extraídos com sucesso');
          setProcessing(false);
          return;
        case 'plan':
          endpoint = `/ai/engine/generate-plan/${actId}`;
          break;
        case 'draft':
          endpoint = `/ai/engine/technical-draft/${actId}`;
          break;
        case 'summary':
          endpoint = `/ai/engine/executive-summary/${actId}`;
          break;
        case 'risk':
          endpoint = `/ai/engine/risk-forecast/${actId}`;
          break;
        case 'precedents':
          const precRes = await api.get(`/ai/engine/precedents/${actId}`);
          setResults(prev => ({ ...prev, precedents: precRes.data }));
          toast.success('Precedentes encontrados');
          setProcessing(false);
          return;
        case 'recurrence':
          const recRes = await api.get(`/ai/engine/recurrence-check/${actId}`);
          setResults(prev => ({ ...prev, recurrence: recRes.data }));
          toast.success('Verificação concluída');
          setProcessing(false);
          return;
        default:
          toast.error('Função não implementada');
          setProcessing(false);
          return;
      }

      const response = await api.post(endpoint);
      setResults(prev => ({ ...prev, [functionId]: response.data }));
      toast.success('Análise concluída');
    } catch (error) {
      console.error('AI function error:', error);
      toast.error('Erro ao executar função de IA');
    } finally {
      setProcessing(false);
    }
  };

  const copyToClipboard = (text) => {
    navigator.clipboard.writeText(JSON.stringify(text, null, 2));
    toast.success('Copiado para área de transferência');
  };

  if (loading) {
    return (
      <AppLayout>
        <div className="space-y-6">
          <Skeleton className="h-10 w-64" />
          <div className="grid grid-cols-4 gap-4">
            {[...Array(8)].map((_, i) => <Skeleton key={i} className="h-24" />)}
          </div>
        </div>
      </AppLayout>
    );
  }

  return (
    <AppLayout>
      <div className="space-y-6" data-testid="ai-engine-page">
        {/* Header */}
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-4">
            {actId && (
              <Button variant="ghost" size="icon" onClick={() => navigate(`/acts/${actId}`)}>
                <ArrowLeft className="w-5 h-5" />
              </Button>
            )}
            <div>
              <div className="flex items-center gap-2">
                <Brain className="w-6 h-6 text-[#1d4ed8]" />
                <h1 className="text-2xl font-bold text-slate-900">AI Governance Engine</h1>
              </div>
              <p className="text-slate-500">
                {act ? `Analisando: ${act.control_body} - ${act.type}` : 'Motor de IA Institucional'}
              </p>
            </div>
          </div>
          {act && (
            <Badge className={
              act.severity === 'CRITICAL' ? 'bg-red-100 text-red-800' :
              act.severity === 'HIGH' ? 'bg-orange-100 text-orange-800' :
              'bg-slate-100 text-slate-800'
            }>
              {act.severity}
            </Badge>
          )}
        </div>

        {/* AI Functions Grid */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
          {AI_FUNCTIONS.map((func) => {
            const Icon = func.icon;
            const hasResult = results[func.id];
            return (
              <Card
                key={func.id}
                className={`cursor-pointer transition-all hover:shadow-md ${
                  activeFunction === func.id ? 'ring-2 ring-[#1d4ed8]' : ''
                } ${hasResult ? 'border-green-200 bg-green-50/50' : ''}`}
                onClick={() => runAIFunction(func.id)}
                data-testid={`ai-function-${func.id}`}
              >
                <CardContent className="p-4">
                  <div className="flex items-center gap-3">
                    <div className={`w-10 h-10 rounded-lg flex items-center justify-center ${
                      hasResult ? 'bg-green-100' : 'bg-[#1d4ed8]/10'
                    }`}>
                      {processing && activeFunction === func.id ? (
                        <Loader2 className="w-5 h-5 text-[#1d4ed8] animate-spin" />
                      ) : (
                        <Icon className={`w-5 h-5 ${hasResult ? 'text-green-600' : 'text-[#1d4ed8]'}`} />
                      )}
                    </div>
                    <div>
                      <p className="text-sm font-medium text-slate-900">{func.name}</p>
                      <p className="text-xs text-slate-500">{func.description}</p>
                    </div>
                  </div>
                  {hasResult && (
                    <Badge variant="outline" className="mt-2 text-xs text-green-600">
                      <CheckCircle className="w-3 h-3 mr-1" />
                      Concluído
                    </Badge>
                  )}
                </CardContent>
              </Card>
            );
          })}
        </div>

        {/* Results Panel */}
        {Object.keys(results).length > 0 && (
          <Card>
            <CardHeader>
              <div className="flex items-center justify-between">
                <CardTitle className="text-lg">Resultados da Análise</CardTitle>
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => copyToClipboard(results)}
                >
                  <Copy className="w-4 h-4 mr-2" />
                  Copiar JSON
                </Button>
              </div>
            </CardHeader>
            <CardContent>
              <Tabs defaultValue={Object.keys(results)[0]} className="w-full">
                <TabsList>
                  {Object.keys(results).map((key) => (
                    <TabsTrigger key={key} value={key} className="capitalize">
                      {AI_FUNCTIONS.find(f => f.id === key)?.name || key}
                    </TabsTrigger>
                  ))}
                </TabsList>

                {Object.entries(results).map(([key, data]) => (
                  <TabsContent key={key} value={key} className="mt-4">
                    {renderResultContent(key, data)}
                  </TabsContent>
                ))}
              </Tabs>
            </CardContent>
          </Card>
        )}

        {/* Quick Actions */}
        {actId && (
          <Card>
            <CardHeader>
              <CardTitle className="text-lg">Ações Rápidas</CardTitle>
            </CardHeader>
            <CardContent className="flex flex-wrap gap-3">
              <Button
                variant="outline"
                onClick={() => runAIFunction('plan')}
                disabled={processing}
              >
                <Target className="w-4 h-4 mr-2" />
                Gerar Plano Completo
              </Button>
              <Button
                variant="outline"
                onClick={() => runAIFunction('risk')}
                disabled={processing}
              >
                <AlertTriangle className="w-4 h-4 mr-2" />
                Analisar Riscos
              </Button>
              <Button
                variant="outline"
                onClick={async () => {
                  try {
                    setProcessing(true);
                    const res = await api.get(`/ai/engine/recurrence-check/${actId}`);
                    setResults(prev => ({ ...prev, recurrence: res.data }));
                    if (res.data.is_recurring) {
                      toast.warning(res.data.alert_message);
                    } else {
                      toast.success('Sem reincidência detectada');
                    }
                  } catch (e) {
                    toast.error('Erro ao verificar');
                  } finally {
                    setProcessing(false);
                  }
                }}
                disabled={processing}
              >
                <RefreshCw className="w-4 h-4 mr-2" />
                Verificar Reincidência
              </Button>
              <Button
                className="bg-[#1d4ed8] hover:bg-[#1e40af]"
                onClick={async () => {
                  try {
                    setProcessing(true);
                    const res = await api.post(`/ai/engine/closure-dossier/${actId}`);
                    setResults(prev => ({ ...prev, dossier: res.data }));
                    toast.success('Dossiê de encerramento gerado');
                  } catch (e) {
                    toast.error('Erro ao gerar dossiê');
                  } finally {
                    setProcessing(false);
                  }
                }}
                disabled={processing}
              >
                <FileText className="w-4 h-4 mr-2" />
                Gerar Dossiê de Encerramento
              </Button>
            </CardContent>
          </Card>
        )}
      </div>
    </AppLayout>
  );
};

// Render specific content based on result type
function renderResultContent(type, data) {
  if (data.error) {
    return (
      <div className="p-4 bg-red-50 rounded-lg text-red-700">
        <p className="font-medium">Erro na análise</p>
        <p className="text-sm">{data.error}</p>
      </div>
    );
  }

  switch (type) {
    case 'classify':
      return (
        <div className="space-y-4">
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
            <div className="p-3 bg-slate-50 rounded-lg">
              <p className="text-xs text-slate-500 uppercase">Tipo</p>
              <p className="font-semibold">{data.type?.replace(/_/g, ' ')}</p>
            </div>
            <div className="p-3 bg-slate-50 rounded-lg">
              <p className="text-xs text-slate-500 uppercase">Severidade</p>
              <p className="font-semibold">{data.severity}</p>
            </div>
            <div className="p-3 bg-slate-50 rounded-lg">
              <p className="text-xs text-slate-500 uppercase">Complexidade</p>
              <p className="font-semibold">{data.complexity_score}/10</p>
            </div>
            <div className="p-3 bg-slate-50 rounded-lg">
              <p className="text-xs text-slate-500 uppercase">Área Temática</p>
              <p className="font-semibold">{data.thematic_area}</p>
            </div>
          </div>
          {data.legal_obligations?.length > 0 && (
            <div>
              <p className="text-sm font-medium text-slate-700 mb-2">Obrigações Legais</p>
              <ul className="list-disc list-inside text-sm text-slate-600">
                {data.legal_obligations.map((ob, i) => <li key={i}>{ob}</li>)}
              </ul>
            </div>
          )}
        </div>
      );

    case 'plan':
      return (
        <div className="space-y-4">
          {data.timeline && (
            <div className="p-4 bg-[#1d4ed8]/5 rounded-lg">
              <p className="text-sm font-medium text-[#1d4ed8]">
                Prazo Total: {data.timeline.total_days} dias
              </p>
            </div>
          )}
          <div className="space-y-2">
            <p className="text-sm font-medium text-slate-700">Ações do Plano</p>
            {data.actions?.map((action, i) => (
              <div key={i} className="p-3 border border-slate-200 rounded-lg">
                <div className="flex items-start justify-between">
                  <div>
                    <p className="font-medium text-slate-900">{i + 1}. {action.description}</p>
                    <p className="text-sm text-slate-500">
                      {action.responsible_unit} • {action.deadline_days} dias
                    </p>
                  </div>
                  {action.evidence_required && (
                    <Badge variant="outline" className="text-xs">Evidência</Badge>
                  )}
                </div>
              </div>
            ))}
          </div>
        </div>
      );

    case 'risk':
      return (
        <div className="space-y-4">
          <div className="flex items-center gap-4">
            <div className={`text-4xl font-bold ${
              data.overall_risk_score >= 70 ? 'text-red-600' :
              data.overall_risk_score >= 40 ? 'text-orange-500' : 'text-green-600'
            }`}>
              {data.overall_risk_score}
            </div>
            <div>
              <p className="text-sm text-slate-500">Score de Risco</p>
              <Badge className={
                data.risk_level === 'CRITICAL' ? 'bg-red-100 text-red-800' :
                data.risk_level === 'HIGH' ? 'bg-orange-100 text-orange-800' :
                data.risk_level === 'MEDIUM' ? 'bg-yellow-100 text-yellow-800' :
                'bg-green-100 text-green-800'
              }>
                {data.risk_level}
              </Badge>
            </div>
          </div>
          <div className="grid grid-cols-2 md:grid-cols-5 gap-3">
            {Object.entries(data.risk_assessment || {}).map(([key, value]) => (
              <div key={key} className="text-center">
                <Progress value={value} className="h-2 mb-1" />
                <p className="text-xs text-slate-500 capitalize">{key.replace(/_/g, ' ')}</p>
                <p className="text-sm font-medium">{value}%</p>
              </div>
            ))}
          </div>
          {data.escalation_need && (
            <div className="p-3 bg-red-50 rounded-lg flex items-center gap-2 text-red-700">
              <AlertTriangle className="w-5 h-5" />
              <span>Escalação necessária para: {data.escalation_to}</span>
            </div>
          )}
        </div>
      );

    case 'precedents':
      return (
        <div className="space-y-3">
          {data.has_recurrence && (
            <div className="p-3 bg-orange-50 rounded-lg text-orange-700">
              <AlertTriangle className="w-4 h-4 inline mr-2" />
              {data.recurrence_warning}
            </div>
          )}
          <p className="text-sm text-slate-500">{data.total_found} precedentes encontrados</p>
          {data.precedents?.map((prec, i) => (
            <div key={i} className="p-3 border border-slate-200 rounded-lg">
              <div className="flex items-center justify-between">
                <div>
                  <p className="font-medium text-slate-900">{prec.control_body}</p>
                  <p className="text-sm text-slate-500">{prec.type} • {prec.thematic_area}</p>
                </div>
                <Badge variant="outline">
                  {prec.similarity_score}% similar
                </Badge>
              </div>
            </div>
          ))}
        </div>
      );

    case 'summary':
      return (
        <div className="space-y-4">
          {data.executive_summary && (
            <>
              <div className="p-4 bg-slate-900 text-white rounded-lg">
                <p className="text-lg font-semibold">{data.executive_summary.headline}</p>
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <p className="text-sm font-medium text-slate-700 mb-2">Situação</p>
                  <p className="text-sm text-slate-600">{data.executive_summary.situation}</p>
                </div>
                <div>
                  <p className="text-sm font-medium text-slate-700 mb-2">Decisão Necessária</p>
                  <p className="text-sm text-slate-600">{data.executive_summary.decision_needed || 'Nenhuma no momento'}</p>
                </div>
              </div>
              {data.executive_summary.next_steps && (
                <div>
                  <p className="text-sm font-medium text-slate-700 mb-2">Próximos Passos</p>
                  <ul className="list-disc list-inside text-sm text-slate-600">
                    {data.executive_summary.next_steps.map((step, i) => <li key={i}>{step}</li>)}
                  </ul>
                </div>
              )}
            </>
          )}
        </div>
      );

    case 'dossier':
      return (
        <div className="space-y-4">
          <div className="p-4 bg-green-50 rounded-lg">
            <p className="text-green-700 font-medium">
              <CheckCircle className="w-4 h-4 inline mr-2" />
              Dossiê gerado: {data.dossier_id}
            </p>
          </div>
          {data.dossier && (
            <div className="space-y-3">
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <p className="text-xs text-slate-500 uppercase">Referência</p>
                  <p className="font-medium">{data.dossier.reference_number}</p>
                </div>
                <div>
                  <p className="text-xs text-slate-500 uppercase">Data</p>
                  <p className="font-medium">{data.dossier.closure_date}</p>
                </div>
              </div>
            </div>
          )}
        </div>
      );

    default:
      return (
        <pre className="p-4 bg-slate-50 rounded-lg overflow-auto text-xs">
          {JSON.stringify(data, null, 2)}
        </pre>
      );
  }
}

export default AIEnginePage;
